import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import { WsTeam } from "../models/wsModels";
import { getPlayersForDraft } from "../services/playersService";
import { formatPlayersForDraft } from "../models/playerModel";
import { toast } from "react-toastify";
import { storeVersion, migrate, onRehydrateStorage } from "./versioning";

const defaultValues = {
  version: storeVersion,
  isBusy: false,
  busyMessage: "",
  message: "",
  timeLeftInSeconds: 0,
  team: null,
  isDraftingOpen: false,
  canTeamOwnerPick: false,
  availablePlayers: [],
  currentSeasonDraft: null,
};

interface DraftingStore {
  version: number;
  message: string;
  isBusy: boolean;
  busyMessage: string;

  timeLeftInSeconds: number;
  setTimeLeftInSeconds: (timeLeftInSeconds: number) => void;

  isDraftingOpen: boolean;

  canTeamOwnerPick: boolean;

  team: WsTeam | null;
  setTeam: (team: WsTeam) => void;
  availablePlayers: any[];
  setAvailablePlayers: (availablePlayers: any[]) => void;
  getAvailablePlayersForDraft: (seasonId: string) => Promise<void>;

  currentSeasonDraft: any;
  setSeasonDraftObject: (seasonDraftObjects: any[]) => void;
}

const useDraftingStore = create<DraftingStore>()(
  devtools(
    persist(
      (set) => ({
        ...defaultValues,
        setTimeLeftInSeconds: (timeLeftInSeconds: number) => {
          set({ timeLeftInSeconds });
        },
        setAvailablePlayers: (availPlayers: any[]) => {
          set({ availablePlayers: formatPlayersForDraft(availPlayers) });
        },
        setTeam: (team: any) => set({ team }),
        getAvailablePlayersForDraft: async (seasonId: string) => {
          try {
            set({ isBusy: true, busyMessage: "Fetching Players..." });
            const response = await getPlayersForDraft(seasonId);
            if (response.code && response.code === 3041) {
              set({
                availablePlayers: formatPlayersForDraft(response.result),
                isBusy: false,
                busyMessage: "",
              });
              return;
            } else {
              set({
                availablePlayers: [],
                isBusy: false,
                busyMessage: "Error fetching Players",
              });
              toast.error(response.message);
            }
          } catch (error) {
            set({
              availablePlayers: [],
              isBusy: false,
              busyMessage: "Error fetching Players",
            });
            console.error("Error fetching Players:", error);
          }
        },
        setSeasonDraftObject: (seasonDraftObject: any) => {
          set({ currentSeasonDraft: seasonDraftObject });
        },
      }),
      {
        name: "draftingStore",
        storage: createJSONStorage(() => localStorage),
        onRehydrateStorage: onRehydrateStorage(defaultValues),
        migrate: migrate(defaultValues),
      }
    ),
    { name: "draftingStore", enabled: process.env.NODE_ENV === "development" }
  )
);

export default useDraftingStore;
